import {
  AlertCircleIcon,
  BriefcaseIcon,
  PlusIcon,
  SearchIcon,
} from 'lucide-react';
import pluralize from 'pluralize';
import { Link, useSearchParams } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { isHiringManagerOnly } from '@/modules/auth/utils/hasRoles';
import {
  companyClientStatusOptions,
  normalizeCompanyClientStatuses,
} from '@/modules/companies/clientStatus';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { JobCardSkeleton } from '@/modules/jobs/components/JobCardSkeleton';
import { JobList } from '@/modules/jobs/components/JobList';
import { useJobFilters } from '@/modules/jobs/hooks/useJobFilters';
import { jobStateOptions } from '@/modules/jobs/jobStates';
import { useInfiniteJobsQuery } from '@/modules/jobs/queries/useInfiniteJobsQuery';
import { ScrollToBottomIntersectionObserver } from '@/modules/pagination/ScrollToBottomIntersectionObserver';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import Container from '@/modules/ui/components/container/Container';
import { EmptyState } from '@/modules/ui/components/empty-state/EmptyState';
import { Input } from '@/modules/ui/components/input/Input';
import { Label } from '@/modules/ui/components/label/Label';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { Switch } from '@/modules/ui/components/switch/Switch';
import { MultiSelect } from '@/modules/ui/MultiSelect';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentUserQuery = useCurrentUserQuery();

  const { filters, updateFilters } = useJobFilters();

  const companiesQuery = useCompaniesQuery();
  const jobsQuery = useInfiniteJobsQuery(filters);
  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    jobs: loadedJobs,
  } = jobsQuery;

  const searchQueryParam = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useDebouncedValue(
    searchQueryParam,
    (value) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (value.trim()) {
        newSearchParams.set('search', value);
      } else {
        newSearchParams.delete('search');
      }
      setSearchParams(newSearchParams);
    },
  );

  const isInitialLoading = isPending;
  const allJobs = loadedJobs;
  const jobs = searchInput.trim()
    ? allJobs.filter((job) =>
        job.title?.toLowerCase().includes(searchInput.toLowerCase()),
      )
    : allJobs;

  const selectedCompany = companiesQuery.data?.find(
    (company) => company.id === filters.companyId,
  );

  const hasMultipleCompanies =
    companiesQuery.data?.length && companiesQuery.data.length > 1;

  const onlyDisplayMyJobs = Boolean(
    filters.userIds?.includes(currentUserQuery.data?.id ?? ''),
  );
  const isHM = isHiringManagerOnly(currentUserQuery.data?.roles ?? []);

  const hasFiltersApplied =
    searchInput.trim() !== '' ||
    filters.companyId !== undefined ||
    filters.userIds !== undefined ||
    filters.state !== undefined ||
    filters.companyClientStatuses !== undefined;
  const hasNoJobs =
    !isInitialLoading &&
    allJobs.length === 0 &&
    !hasNextPage &&
    !hasFiltersApplied;
  const hasNoMatchingJobs =
    !isInitialLoading && jobs.length === 0 && !hasNoJobs && !hasNextPage;

  return (
    <Container>
      <PageHeader
        title={selectedCompany?.name ? `${selectedCompany.name}` : 'Jobs'}
        action={
          <div className="flex gap-2">
            <PermissionsGate permissions={[permissions.Jobs.StartSourcing]}>
              <Button variant="outline" size="sm" asChild>
                <Link to="/jobs/scout/new">
                  <SearchIcon />
                  Scout sourcing
                </Link>
              </Button>
            </PermissionsGate>
            <PermissionsGate permissions={[permissions.Jobs.Create]}>
              <Button asChild>
                <Link to="/jobs/add">
                  <PlusIcon />
                  Add Job
                </Link>
              </Button>
            </PermissionsGate>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>Failed to load jobs</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-4">
        {!hasNoJobs && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <SearchIcon className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder="Search jobs..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 sm:w-50"
                />
              </div>

              <div className="flex items-center gap-2 rounded-md border px-3 py-2 transition-colors">
                <Label
                  htmlFor="my-jobs-switch"
                  className="cursor-pointer font-normal"
                >
                  Only show my jobs
                </Label>
                <Switch
                  id="my-jobs-switch"
                  checked={onlyDisplayMyJobs}
                  onCheckedChange={(checked) => {
                    updateFilters({
                      userIds: checked
                        ? [currentUserQuery.data?.id ?? '']
                        : undefined,
                    });
                  }}
                />
              </div>

              {(isInitialLoading || hasMultipleCompanies) && (
                <Combobox
                  value={filters?.companyId || 'all'}
                  placeholder="Filter by company..."
                  searchPlaceholder="Search company..."
                  emptyMessage="No company found."
                  disabled={isInitialLoading}
                  className="w-full sm:w-[200px]"
                  disableSort
                  options={[
                    { value: 'all', label: 'All Companies' },
                    ...(companiesQuery.data ?? [])
                      .map((company) => ({
                        value: company.id,
                        label: company.name ?? '',
                      }))
                      .sort((a, b) =>
                        a.label.localeCompare(b.label, undefined, {
                          numeric: true,
                        }),
                      ),
                  ]}
                  onChange={(value) =>
                    updateFilters({
                      companyId: value === 'all' ? undefined : value,
                    })
                  }
                />
              )}

              {!isHM && (
                <Combobox
                  value={filters.state ?? 'all'}
                  placeholder="Filter by job state..."
                  searchPlaceholder="Search job state..."
                  emptyMessage="No job state found."
                  disabled={isInitialLoading}
                  className="w-full sm:w-[180px]"
                  disableSort
                  options={[
                    { value: 'all', label: 'All Job States' },
                    ...jobStateOptions.map(({ label, value }) => ({
                      value,
                      label,
                    })),
                  ]}
                  onChange={(value) =>
                    updateFilters({
                      state:
                        value === 'all'
                          ? undefined
                          : jobStateOptions.find(
                              (option) => option.value === value,
                            )?.value,
                    })
                  }
                />
              )}

              <MultiSelect
                options={companyClientStatusOptions}
                defaultValue={filters.companyClientStatuses}
                onValueChange={(values) =>
                  updateFilters({
                    companyClientStatuses:
                      normalizeCompanyClientStatuses(values),
                  })
                }
                placeholder="Filter by client status..."
                maxCount={1}
                searchable={false}
                hideSelectAll
                autoSize
                singleLine
                minWidth="180px"
                maxWidth="200px"
              />
            </div>

            {isInitialLoading ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <div className="text-muted-foreground shrink-0 text-sm font-medium">
                {jobs.length} {pluralize('job', jobs.length)} shown
              </div>
            )}
          </div>
        )}

        {isInitialLoading ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <JobCardSkeleton key={index} />
            ))}
          </div>
        ) : hasNoJobs ? (
          <EmptyState
            icon={<BriefcaseIcon className="size-12" />}
            title="No jobs created yet"
            description="Get started by creating your first job, and find the perfect candidates."
            action={{
              label: 'Add Job',
              href: '/jobs/add',
            }}
          />
        ) : hasNoMatchingJobs ? (
          <EmptyState
            icon={<BriefcaseIcon className="size-12" />}
            title="No jobs found"
            description="No jobs match your current filters. Try adjusting your search or filters."
          />
        ) : (
          <>
            <JobList jobs={jobs} />

            {isFetchingNextPage && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                {[...Array(3)].map((_, index) => (
                  <JobCardSkeleton key={index} />
                ))}
              </div>
            )}
          </>
        )}

        {hasNextPage && (
          <ScrollToBottomIntersectionObserver
            enabled={hasNextPage && !isFetchingNextPage}
            isLoading={isFetchingNextPage}
            onIntersect={() => fetchNextPage()}
          />
        )}
      </div>
    </Container>
  );
};

export default Jobs;
